import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import {
  categoryStandingsService,
  type CategoryStandings,
  type StandingTie,
  type Tiebreak,
} from '../../services/category-standings';
import type { StandingsSection } from '../../utils/category-standings';

interface TiebreaksPanelProps {
  section: StandingsSection;
  competitionSlug: string;
  categoryId: string;
  /** The athletes' names, by entry id. */
  names: Map<string, string>;
  /** Every change answers with the class's standings: the page redraws from it. */
  onChange: (standings: CategoryStandings) => void;
}

/** The name a tie-break goes by: the place it was called for, and "(2)" when it is the second. */
function useTiebreakName() {
  const { t } = useTranslation();
  return (tiebreak: Pick<Tiebreak, 'rank' | 'sequence'>) =>
    tiebreak.sequence > 1
      ? t('tiebreaks.nameSequence', { rank: tiebreak.rank, sequence: tiebreak.sequence })
      : t('tiebreaks.name', { rank: tiebreak.rank });
}

/**
 * A table's tie-breaks, under it on "Classifiche" (issue #364): the ties still
 * level, each with a way to call one, and every tie-break with its outcome.
 *
 * Two steps, as on the day: calling one is calling the athletes ("spareggio in
 * corso" on the standings), and the outcome is typed in when the judges say
 * who won — a position per athlete, equal ones for those still level, who then
 * get a tie of their own for the next one. Only once every round of the table
 * is under review or closed: until then the athletes may still change the
 * ties. Nothing here prints.
 */
export function TiebreaksPanel({ section, competitionSlug, categoryId, names, onChange }: TiebreaksPanelProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const nameOf = useTiebreakName();

  const [calling, setCalling] = useState<StandingTie | null>(null);
  const [deciding, setDeciding] = useState<Tiebreak | null>(null);
  const [deleting, setDeleting] = useState<Tiebreak | null>(null);
  const [busy, setBusy] = useState(false);

  const open = section.ties.filter((tie) => tie.tiebreak_id === null);
  if (open.length === 0 && section.tiebreaks.length === 0) return null;

  const athletes = (ids: string[]) => ids.map((id) => names.get(id) ?? '—').join(', ');

  const run = async (action: () => Promise<CategoryStandings>, done: () => void) => {
    setBusy(true);
    try {
      onChange(await action());
      done();
    } catch (error) {
      toast({
        title: t('errors.actionFailed'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border p-3 print:hidden" data-testid="tiebreaks-panel">
      <h4 className="text-sm font-semibold">{t('tiebreaks.title')}</h4>

      {open.length > 0 && !section.canAddTiebreak && section.roundsNotEnded.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="tiebreaks-waiting">
          {t('tiebreaks.waitingFor', { names: section.roundsNotEnded.join(', ') })}
        </p>
      )}

      <ul className="divide-y text-sm">
        {open.map((tie) => (
          <li
            key={tie.participant_ids.join(',')}
            className="flex flex-wrap items-center justify-between gap-2 py-2"
            data-testid="tiebreak-open-tie"
          >
            <span>
              <span className="font-medium">{t('tiebreaks.tie', { rank: tie.rank })}</span>
              <span className="text-muted-foreground"> · {athletes(tie.participant_ids)}</span>
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={!section.canAddTiebreak || busy}
              onClick={() => setCalling(tie)}
              data-testid="tiebreak-call"
            >
              {t('tiebreaks.call')}
            </Button>
          </li>
        ))}

        {section.tiebreaks.map((tiebreak) => (
          <li key={tiebreak.id} className="space-y-1 py-2" data-testid="tiebreak-row">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{nameOf(tiebreak)}</span>
                {tiebreak.stale ? (
                  <Badge variant="outline" className="font-normal text-muted-foreground">
                    {t('tiebreaks.staleBadge')}
                  </Badge>
                ) : tiebreak.decided ? (
                  <Badge variant="secondary" className="font-normal">
                    {t('tiebreaks.decidedBadge')}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-amber-500 font-normal text-amber-700 dark:text-amber-300">
                    {t('tiebreaks.pending')}
                  </Badge>
                )}
              </span>
              <span className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!section.canAddTiebreak || busy}
                  onClick={() => setDeciding(tiebreak)}
                  data-testid="tiebreak-decide"
                >
                  {tiebreak.decided ? t('tiebreaks.edit') : t('tiebreaks.decide')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy || tiebreak.has_follow_ups}
                  title={tiebreak.has_follow_ups ? t('tiebreaks.hasFollowUps') : undefined}
                  onClick={() => setDeleting(tiebreak)}
                  data-testid="tiebreak-delete"
                >
                  {t('tiebreaks.delete')}
                </Button>
              </span>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="tiebreak-outcome">
              {tiebreak.decided
                ? [...tiebreak.entries]
                    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                    .map((entry) => `${entry.position}° ${names.get(entry.participant_id) ?? '—'}`)
                    .join(' · ')
                : athletes(tiebreak.entries.map((entry) => entry.participant_id))}
            </p>
            {tiebreak.stale && (
              <p className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300" data-testid="tiebreak-stale">
                <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
                {t('tiebreaks.stale')}
              </p>
            )}
          </li>
        ))}
      </ul>

      {/* Calling the athletes. */}
      <Dialog open={calling !== null} onOpenChange={(next) => !next && setCalling(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{calling && t('tiebreaks.name', { rank: calling.rank })}</DialogTitle>
            <DialogDescription>
              {calling && t('tiebreaks.callDescription', { rank: calling.rank, names: athletes(calling.participant_ids) })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalling(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={busy}
              onClick={() =>
                calling &&
                run(
                  () =>
                    categoryStandingsService.addTiebreak(
                      competitionSlug,
                      categoryId,
                      section.scopeRoundId,
                      calling.participant_ids
                    ),
                  () => setCalling(null)
                )
              }
              data-testid="tiebreak-confirm-call"
            >
              {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('tiebreaks.call')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {deciding && (
        <OutcomeDialog
          tiebreak={deciding}
          title={nameOf(deciding)}
          names={names}
          busy={busy}
          onCancel={() => setDeciding(null)}
          onSave={(positions) =>
            run(
              () => categoryStandingsService.decideTiebreak(competitionSlug, categoryId, deciding.id, positions),
              () => setDeciding(null)
            )
          }
        />
      )}

      {/* Its athletes are level again. */}
      <Dialog open={deleting !== null} onOpenChange={(next) => !next && setDeleting(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('tiebreaks.deleteTitle')}</DialogTitle>
            <DialogDescription>{t('tiebreaks.deleteDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() =>
                deleting &&
                run(
                  () => categoryStandingsService.deleteTiebreak(competitionSlug, categoryId, deleting.id),
                  () => setDeleting(null)
                )
              }
              data-testid="tiebreak-confirm-delete"
            >
              {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('tiebreaks.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The outcome: a place in the tie-break for each athlete. Equal places are the
 * athletes still level, which the dialog says rather than refuses.
 */
function OutcomeDialog({
  tiebreak,
  title,
  names,
  busy,
  onCancel,
  onSave,
}: {
  tiebreak: Tiebreak;
  title: string;
  names: Map<string, string>;
  busy: boolean;
  onCancel: () => void;
  onSave: (positions: Record<string, number>) => void;
}) {
  const { t } = useTranslation();
  const [positions, setPositions] = useState<Record<string, number | null>>(() =>
    Object.fromEntries(tiebreak.entries.map((entry) => [entry.participant_id, entry.position]))
  );

  const choices = tiebreak.entries.map((_, index) => index + 1);
  const complete = Object.values(positions).every((position) => position != null);
  const level = complete && new Set(Object.values(positions)).size < tiebreak.entries.length;

  return (
    <Dialog open onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t('tiebreaks.decideDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {tiebreak.entries.map((entry) => (
            <div key={entry.participant_id} className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium">{names.get(entry.participant_id) ?? '—'}</span>
              <Select
                value={positions[entry.participant_id]?.toString() ?? ''}
                onValueChange={(value) =>
                  setPositions((current) => ({ ...current, [entry.participant_id]: Number(value) }))
                }
              >
                <SelectTrigger className="w-24" data-testid={`tiebreak-position-${entry.participant_id}`}>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {choices.map((choice) => (
                    <SelectItem key={choice} value={choice.toString()}>
                      {t('tiebreaks.position', { position: choice })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>

        {level && (
          <p className="text-xs text-muted-foreground" data-testid="tiebreak-still-level">
            {t('tiebreaks.stillLevel')}
          </p>
        )}
        {tiebreak.has_follow_ups && (
          <p className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
            {t('tiebreaks.followUpWarning')}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={busy || !complete}
            onClick={() => onSave(positions as Record<string, number>)}
            data-testid="tiebreak-confirm-decide"
          >
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TiebreaksPanel;
